/**
 * Sidebar Constants
 *
 * Constants and utility functions for sidebar navigation.
 * Separated from Sidebar component to avoid React Fast Refresh warnings.
 */

export interface SidebarItem {
  key: string;
  label: string;
  icon: string;
  path: string;
  badge?: string;
  children?: SidebarItem[];
}

// Default sidebar items based on the HTML prototype
export const getDefaultSidebarItems = (t: (key: string) => string): SidebarItem[] => {
  // Check for debug mode - fallback to false if not available
  const isDebug =
    typeof import.meta !== 'undefined' &&
    (import.meta as { env?: { VITE_DEBUG?: string } }).env &&
    (import.meta as { env?: { VITE_DEBUG?: string } }).env?.VITE_DEBUG === 'true';

  const baseItems: SidebarItem[] = [
    {
      key: 'dashboard',
      label: t('navigation.dashboard'),
      icon: 'dashboard',
      path: '/dashboard',
    },
    {
      key: 'agents',
      label: t('navigation.agents'),
      icon: 'headset_mic',
      path: '/agents',
      children: [
        {
          key: 'agent-management',
          label: t('navigation.agentManagement'),
          icon: 'manage_accounts',
          path: '/agents',
        },
        {
          key: 'call-routing',
          label: t('navigation.callRouting'),
          icon: 'phone',
          path: '/call-routing',
        },
        {
          key: 'phone-numbers',
          label: t('navigation.phoneNumbers'),
          icon: 'phone_android',
          path: '/phone-numbers',
        },
        {
          key: 'calendars',
          label: t('navigation.calendars'),
          icon: 'event',
          path: '/calendars',
        },
        {
          key: 'knowledge-base',
          label: t('navigation.knowledgeBase'),
          icon: 'book',
          path: '/knowledge-base',
        },
        {
          key: 'communications',
          label: t('navigation.communications'),
          icon: 'message',
          path: '/communications',
        },
      ],
    },
    {
      key: 'calls',
      label: t('navigation.calls'),
      icon: 'phone',
      path: '/calls',
    },
    {
      key: 'users',
      label: t('navigation.users'),
      icon: 'people',
      path: '/users',
    },
    {
      key: 'analytics',
      label: t('navigation.analytics'),
      icon: 'analytics',
      path: '/analytics',
    },
    {
      key: 'billing',
      label: t('navigation.billing'),
      icon: 'credit_card',
      path: '/billing',
    },
  ];

  // Add development section if debug mode is enabled
  if (isDebug) {
    baseItems.push({
      key: 'development',
      label: 'Development',
      icon: 'code',
      path: '/design-system',
      children: [
        {
          key: 'design-system',
          label: 'Design System',
          icon: 'palette',
          path: '/design-system',
        },
      ],
    });
  }

  return baseItems;
};
